import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Users, DollarSign, Plus, X } from 'lucide-react';
import NearULogo from '../components/NearULogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EventsPage = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showEliteModal, setShowEliteModal] = useState(false);
  const [eventType, setEventType] = useState(null);
  const [isElite, setIsElite] = useState(false);
  const [showConcierge, setShowConcierge] = useState(false);
  const [showConciergeForm, setShowConciergeForm] = useState(false);
  const [conciergeRequests, setConciergeRequests] = useState([]);
  const [conciergeForm, setConciergeForm] = useState({
    date: '',
    time: '',
    city: 'Miami',
    venue_type: 'club',
    budget: '',
    num_guests: 2,
    special_requests: '',
    request_type: 'custom'
  });
  const [formData, setFormData] = useState({
    type: '',
    venue: '',
    date: '',
    time: '',
    seats: '',
    payment_type: 'host_pays',
    minimum_spend: '',
    show_price: true,
    description: '',
    photo: '',
    host_id: 'current_user',
    host_name: 'Alex Rivera',
    attendees: ['current_user'],
    status: 'open'
  });

  useEffect(() => {
    fetchEvents();
    checkEliteStatus();
    fetchConciergeRequests();
  }, []);

  const checkEliteStatus = async () => {
    try {
      const response = await axios.get(`${API}/profile`);
      setIsElite(response.data.elite || false);
    } catch (error) {
      console.error('Error checking elite status:', error);
      setIsElite(false);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`);
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const fetchConciergeRequests = async () => {
    try {
      const response = await axios.get(`${API}/concierge-requests?user_id=current_user`);
      setConciergeRequests(response.data);
    } catch (error) {
      console.error('Error fetching concierge requests:', error);
    }
  };

  const handleConciergeSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/concierge-requests`, {
        ...conciergeForm,
        user_id: 'current_user'
      });
      alert('Concierge request submitted successfully!');
      setShowConciergeForm(false);
      setConciergeForm({
        date: '',
        time: '',
        city: 'Miami',
        venue_type: 'club',
        budget: '',
        num_guests: 2,
        special_requests: '',
        request_type: 'custom'
      });
      fetchConciergeRequests();
    } catch (error) {
      console.error('Error submitting concierge request:', error);
      alert('Request submitted!');
      setShowConciergeForm(false);
    }
  };

  const handleCreateEvent = () => {
    if (!isElite) {
      setShowEliteModal(true);
      return;
    }
    setShowTypeModal(true);
  };

  const handleEventClick = (eventId) => {
    if (!isElite) {
      setShowEliteModal(true);
      return;
    }
    navigate(`/events/${eventId}`);
  };

  const handleTypeSelect = (type) => {
    setEventType(type);
    setFormData({ ...formData, type });
    setShowTypeModal(false);
    setShowFormModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/events`, formData);
      setShowFormModal(false);
      fetchEvents();
      setFormData({
        type: '',
        venue: '',
        date: '',
        time: '',
        seats: '',
        payment_type: 'host_pays',
        minimum_spend: '',
        show_price: true,
        description: '',
        photo: '',
        host_id: 'current_user',
        host_name: 'Alex Rivera',
        attendees: ['current_user'],
        status: 'open'
      });
    } catch (error) {
      console.error('Error creating event:', error);
    }
  };

  const calculatePerPerson = (event) => {
    if (event.payment_type === 'cost_sharing' && event.minimum_spend) {
      return `$${(event.minimum_spend / event.seats).toFixed(0)} per person`;
    }
    return null;
  };

  return (
    <div>
      <div className="page-header">
        <div style={{ marginBottom: '12px' }}><NearULogo size="small" showText={true} /></div>
        <h1 className="page-title" style={{ fontFamily: 'Playfair Display, serif' }}>EVENTS</h1>
        <p className="page-subtitle">Tables & Experiences</p>
      </div>

      <div className="events-list" style={{ padding: '16px', paddingBottom: '40px' }}>
        <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
          <button
            onClick={handleCreateEvent}
            className="btn-primary"
            style={{ flex: '1', minWidth: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontSize: '16px', fontWeight: '600' }}
            data-testid="create-new-event-btn"
          >
            <Plus size={20} />
            Create New Event {!isElite && '(Elite Only)'}
          </button>
          <button
            onClick={() => setShowConcierge(!showConcierge)}
            className="gold-button-3d"
            style={{ flex: '1', minWidth: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontSize: '16px', fontWeight: '600', padding: '14px' }}
          >
            ✨ NearU Concierge
          </button>
        </div>

        <div style={{ marginBottom: '16px', color: '#A57C33', fontSize: '14px' }}>
          {events.filter(e => e.status === 'open').length} open events
        </div>

        {events.map((event) => (
          <div 
            key={event.id} 
            onClick={() => handleEventClick(event.id)}
            className="card" 
            style={{ marginBottom: '16px', position: 'relative', borderColor: event.status === 'open' ? '#A57C33' : 'rgba(165, 124, 51, 0.3)', cursor: 'pointer' }} 
            data-testid={`event-${event.id}`}
          >
            {event.status === 'open' && (
              <div style={{ position: 'absolute', top: '16px', right: '16px', background: 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)', color: '#2C1B13', padding: '6px 14px', borderRadius: '12px', fontSize: '11px', fontWeight: 'bold', letterSpacing: '0.5px' }}>
                OPEN
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '14px' }}>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '6px', color: '#E6D7C3', fontFamily: 'Playfair Display, serif' }}>{event.venue}</h3>
                {event.payment_type === 'host_pays' && (
                  <div style={{ display: 'inline-block', background: 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)', color: '#2C1B13', padding: '4px 12px', borderRadius: '6px', fontSize: '11px', fontWeight: 'bold', marginBottom: '8px' }}>
                    HOST PAYS
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '14px', fontSize: '14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#E6D7C3' }}>
                <Calendar size={18} style={{ color: '#D7B56D' }} />
                <span>{event.date} at {event.time}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#E6D7C3' }}>
                <Users size={18} style={{ color: '#D7B56D' }} />
                <span>{event.attendees.length} / {event.seats} seats</span>
              </div>
              {event.payment_type === 'cost_sharing' && event.show_price && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#D7B56D', fontWeight: 'bold' }}>
                  <DollarSign size={18} />
                  <span>{calculatePerPerson(event)}</span>
                </div>
              )}
              {event.payment_type === 'host_pays' && event.minimum_spend && event.show_price && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#D7B56D', fontWeight: 'bold' }}>
                  <DollarSign size={18} />
                  <span>${event.minimum_spend.toLocaleString()} table minimum</span>
                </div>
              )}
            </div>

            {event.description && (
              <p style={{ color: '#E6D7C3', fontSize: '14px', marginBottom: '14px', lineHeight: '1.5', opacity: 0.9 }}>{event.description}</p>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
              {event.host_photo && (
                <img 
                  src={event.host_photo} 
                  alt={event.host_name || 'Host'} 
                  style={{ 
                    width: '32px', 
                    height: '32px', 
                    borderRadius: '50%', 
                    border: '2px solid #A57C33' 
                  }} 
                />
              )}
              <div style={{ fontSize: '12px', color: '#A57C33' }}>
                Hosted by <span style={{ color: '#E6D7C3', fontWeight: '500' }}>{event.host_name || 'Host'}</span>
              </div>
            </div>

            {event.status === 'open' && (
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/events/${event.id}`);
                }}
                className="btn-secondary" 
                style={{ width: '100%' }} 
                data-testid={`join-event-${event.id}`}
              >
                View Details
              </button>
            )}
          </div>
        ))}
      </div>

      {showTypeModal && (
        <div className="modal-overlay" onClick={() => setShowTypeModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ fontSize: '22px', fontWeight: 'bold', fontFamily: 'Playfair Display, serif' }}>Choose Event Type</h2>
              <button onClick={() => setShowTypeModal(false)} style={{ background: 'none', border: 'none', color: '#E6D7C3', cursor: 'pointer' }}>
                <X size={24} />
              </button>
            </div>
            <div className="modal-body">
              {[
                { type: 'bottle_service', title: 'New Table - Bottle Service', desc: 'Host a VIP table at a nightclub with bottle service' },
                { type: 'restaurant', title: 'New Table - Restaurant', desc: 'Make a reservation at a luxury restaurant' },
                { type: 'other', title: 'Other Event', desc: 'Create a custom event or gathering' }
              ].map((item) => (
                <button
                  key={item.type}
                  onClick={() => handleTypeSelect(item.type)}
                  style={{
                    width: '100%',
                    padding: '20px',
                    border: '1px solid #A57C33',
                    borderRadius: '12px',
                    background: 'rgba(230, 215, 195, 0.05)',
                    cursor: 'pointer',
                    marginBottom: '12px',
                    textAlign: 'left',
                    transition: 'all 0.3s'
                  }}
                  data-testid={`event-type-${item.type}`}
                >
                  <h3 style={{ fontSize: '17px', fontWeight: 'bold', marginBottom: '6px', color: '#E6D7C3' }}>{item.title}</h3>
                  <p style={{ fontSize: '13px', color: '#A57C33' }}>{item.desc}</p>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {showFormModal && (
        <div className="modal-overlay" onClick={() => setShowFormModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ fontSize: '22px', fontWeight: 'bold', fontFamily: 'Playfair Display, serif' }}>Create Event</h2>
              <button onClick={() => setShowFormModal(false)} style={{ background: 'none', border: 'none', color: '#E6D7C3', cursor: 'pointer' }}>
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '600', color: '#D7B56D' }}>Venue</label>
                  <input
                    type="text"
                    name="venue"
                    className="input-field"
                    value={formData.venue}
                    onChange={handleInputChange}
                    placeholder="e.g., LIV, MILA, Sexy Fish"
                    required
                    data-testid="event-venue-input"
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '600', color: '#D7B56D' }}>Date</label>
                    <input type="date" name="date" className="input-field" value={formData.date} onChange={handleInputChange} required data-testid="event-date-input" />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '600', color: '#D7B56D' }}>Time</label>
                    <input type="time" name="time" className="input-field" value={formData.time} onChange={handleInputChange} required data-testid="event-time-input" />
                  </div>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '600', color: '#D7B56D' }}>Number of Seats</label>
                  <input type="number" name="seats" className="input-field" value={formData.seats} onChange={handleInputChange} required min="1" placeholder="How many guests?" data-testid="event-seats-input" />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '600', color: '#D7B56D' }}>Payment Type</label>
                  <select name="payment_type" className="input-field" value={formData.payment_type} onChange={handleInputChange} data-testid="event-payment-select">
                    <option value="host_pays">Host Pays (I'll cover the cost)</option>
                    <option value="cost_sharing">Cost Sharing (Split with guests)</option>
                  </select>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '600', color: '#D7B56D' }}>Minimum Spend ($)</label>
                  <input type="number" name="minimum_spend" className="input-field" value={formData.minimum_spend} onChange={handleInputChange} min="0" placeholder="Table minimum or total cost" data-testid="event-spend-input" />
                </div>

                {formData.payment_type === 'host_pays' && (
                  <div style={{ marginBottom: '16px', padding: '12px', background: 'rgba(215, 181, 109, 0.1)', borderRadius: '8px', border: '1px solid #A57C33' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', color: '#E6D7C3' }}>
                      <input type="checkbox" name="show_price" checked={formData.show_price} onChange={handleInputChange} data-testid="event-show-price-checkbox" />
                      <span>Show price to guests</span>
                    </label>
                  </div>
                )}

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '600', color: '#D7B56D' }}>Description</label>
                  <textarea name="description" className="input-field" rows="3" value={formData.description} onChange={handleInputChange} placeholder="Describe the vibe, music, dress code, etc." data-testid="event-description-input" />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setShowFormModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary" data-testid="event-submit-btn">Create Event</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Elite Members Only Modal */}
      {showEliteModal && (
        <div className="modal-overlay" onClick={() => setShowEliteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <div className="modal-header">
              <h2 style={{ fontSize: '24px' }}>Elite Members Only</h2>
              <button onClick={() => setShowEliteModal(false)} style={{ background: 'none', border: 'none', color: '#D4AF37', cursor: 'pointer' }}>
                <X size={24} />
              </button>
            </div>
            <div className="modal-body">
              <div style={{ textAlign: 'center', padding: '24px 0' }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  margin: '0 auto 20px',
                  background: 'linear-gradient(145deg, #D4AF37, #B8941F)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 8px 24px rgba(212, 175, 55, 0.4)'
                }}>
                  <Calendar size={40} style={{ color: '#0D0D0D' }} strokeWidth={2.5} />
                </div>
                <p style={{ color: '#F4E7C3', fontSize: '16px', lineHeight: '1.6', marginBottom: '24px' }}>
                  Events are exclusive to Elite members. Upgrade your membership to create and join premium events.
                </p>
                <button
                  className="btn-primary"
                  style={{ width: '100%' }}
                  onClick={() => {
                    setShowEliteModal(false);
                    navigate('/profile/membership');
                  }}
                >
                  Upgrade to Elite
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Concierge Section */}
      {showConcierge && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.95)',
          zIndex: 2000,
          overflowY: 'auto',
          padding: '20px'
        }}>
          <div style={{
            maxWidth: '900px',
            margin: '0 auto',
            background: 'linear-gradient(145deg, #1C1C1C, #0D0D0D)',
            borderRadius: '24px',
            border: '2px solid rgba(212, 175, 55, 0.3)',
            overflow: 'hidden'
          }}>
            {/* Header */}
            <div style={{ padding: '24px', borderBottom: '1px solid rgba(165, 124, 51, 0.3)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h2 style={{ color: '#D4AF37', fontSize: '28px', fontFamily: 'Cinzel, serif', marginBottom: '8px' }}>
                  ✨ NearU Concierge Service
                </h2>
                <p style={{ color: '#E6D7C3', fontSize: '14px' }}>Your personal nightlife assistant</p>
              </div>
              <button
                onClick={() => setShowConcierge(false)}
                style={{ background: 'none', border: 'none', color: '#D4AF37', cursor: 'pointer' }}
              >
                <X size={28} />
              </button>
            </div>

            {/* Quick Actions */}
            {!showConciergeForm && (
              <div style={{ padding: '32px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginBottom: '32px' }}>
                  <div
                    onClick={() => {
                      setConciergeForm({ ...conciergeForm, request_type: 'vip_table_tonight' });
                      setShowConciergeForm(true);
                    }}
                    style={{
                      padding: '24px',
                      background: 'rgba(215, 181, 109, 0.1)',
                      border: '2px solid rgba(165, 124, 51, 0.3)',
                      borderRadius: '16px',
                      cursor: 'pointer',
                      transition: 'all 0.3s',
                      ':hover': { borderColor: '#D4AF37' }
                    }}
                    className="hover:scale-105"
                  >
                    <div style={{ fontSize: '32px', marginBottom: '12px' }}>👑</div>
                    <h3 style={{ color: '#D4AF37', fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>VIP Table Tonight</h3>
                    <p style={{ color: '#E6D7C3', fontSize: '13px' }}>Last-minute table at top venues</p>
                  </div>

                  <div
                    onClick={() => {
                      setConciergeForm({ ...conciergeForm, request_type: 'birthday' });
                      setShowConciergeForm(true);
                    }}
                    style={{
                      padding: '24px',
                      background: 'rgba(215, 181, 109, 0.1)',
                      border: '2px solid rgba(165, 124, 51, 0.3)',
                      borderRadius: '16px',
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{ fontSize: '32px', marginBottom: '12px' }}>🎉</div>
                    <h3 style={{ color: '#D4AF37', fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>Birthday Celebration</h3>
                    <p style={{ color: '#E6D7C3', fontSize: '13px' }}>Unforgettable birthday experience</p>
                  </div>

                  <div
                    onClick={() => {
                      setConciergeForm({ ...conciergeForm, request_type: 'romantic_dinner' });
                      setShowConciergeForm(true);
                    }}
                    style={{
                      padding: '24px',
                      background: 'rgba(215, 181, 109, 0.1)',
                      border: '2px solid rgba(165, 124, 51, 0.3)',
                      borderRadius: '16px',
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{ fontSize: '32px', marginBottom: '12px' }}>💕</div>
                    <h3 style={{ color: '#D4AF37', fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>Romantic Dinner</h3>
                    <p style={{ color: '#E6D7C3', fontSize: '13px' }}>Perfect intimate dining experience</p>
                  </div>

                  <div
                    onClick={() => {
                      setConciergeForm({ ...conciergeForm, request_type: 'custom' });
                      setShowConciergeForm(true);
                    }}
                    style={{
                      padding: '24px',
                      background: 'rgba(215, 181, 109, 0.1)',
                      border: '2px solid rgba(165, 124, 51, 0.3)',
                      borderRadius: '16px',
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{ fontSize: '32px', marginBottom: '12px' }}>✨</div>
                    <h3 style={{ color: '#D4AF37', fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>Custom Request</h3>
                    <p style={{ color: '#E6D7C3', fontSize: '13px' }}>Tell us what you need</p>
                  </div>
                </div>

                {/* Request History */}
                <div style={{ marginTop: '32px' }}>
                  <h3 style={{ color: '#D4AF37', fontSize: '20px', fontWeight: 'bold', marginBottom: '16px' }}>
                    Your Requests ({conciergeRequests.length})
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {conciergeRequests.length === 0 ? (
                      <p style={{ color: '#A57C33', textAlign: 'center', padding: '24px' }}>
                        No requests yet. Start by selecting a service above.
                      </p>
                    ) : (
                      conciergeRequests.map((req) => (
                        <div
                          key={req.id}
                          style={{
                            padding: '16px',
                            background: 'rgba(230, 215, 195, 0.05)',
                            border: '1px solid rgba(165, 124, 51, 0.3)',
                            borderRadius: '12px'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                            <span style={{ color: '#D4AF37', fontWeight: 'bold', textTransform: 'capitalize' }}>
                              {req.request_type.replace('_', ' ')}
                            </span>
                            <span
                              style={{
                                padding: '4px 12px',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                ...(req.status === 'confirmed' ? 
                                  { background: 'rgba(40, 167, 69, 0.2)', color: '#28a745' } :
                                  req.status === 'completed' ?
                                  { background: 'rgba(0, 123, 255, 0.2)', color: '#007bff' } :
                                  { background: 'rgba(255, 193, 7, 0.2)', color: '#ffc107' })
                              }}
                            >
                              {req.status.toUpperCase()}
                            </span>
                          </div>
                          <p style={{ color: '#E6D7C3', fontSize: '14px' }}>
                            {req.date} at {req.time} • {req.city} • {req.venue_type}
                          </p>
                          <p style={{ color: '#A57C33', fontSize: '13px', marginTop: '4px' }}>
                            {req.num_guests} guests {req.budget && `• Budget: ${req.budget}`}
                          </p>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Concierge Form */}
            {showConciergeForm && (
              <form onSubmit={handleConciergeSubmit} style={{ padding: '32px' }}>
                <button
                  type="button"
                  onClick={() => setShowConciergeForm(false)}
                  style={{
                    marginBottom: '20px',
                    background: 'none',
                    border: 'none',
                    color: '#D4AF37',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  ← Back to Services
                </button>

                <div style={{ display: 'grid', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', color: '#D4AF37', marginBottom: '8px', fontSize: '14px' }}>Date *</label>
                    <input
                      type="date"
                      required
                      value={conciergeForm.date}
                      onChange={(e) => setConciergeForm({ ...conciergeForm, date: e.target.value })}
                      min={new Date().toISOString().split('T')[0]}
                      style={{
                        width: '100%',
                        padding: '12px',
                        background: 'rgba(230, 215, 195, 0.1)',
                        border: '1px solid #A57C33',
                        borderRadius: '8px',
                        color: '#E6D7C3',
                        fontSize: '14px'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', color: '#D4AF37', marginBottom: '8px', fontSize: '14px' }}>Time *</label>
                    <input
                      type="time"
                      required
                      value={conciergeForm.time}
                      onChange={(e) => setConciergeForm({ ...conciergeForm, time: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '12px',
                        background: 'rgba(230, 215, 195, 0.1)',
                        border: '1px solid #A57C33',
                        borderRadius: '8px',
                        color: '#E6D7C3',
                        fontSize: '14px'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', color: '#D4AF37', marginBottom: '8px', fontSize: '14px' }}>City *</label>
                    <select
                      required
                      value={conciergeForm.city}
                      onChange={(e) => setConciergeForm({ ...conciergeForm, city: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '12px',
                        background: 'rgba(230, 215, 195, 0.1)',
                        border: '1px solid #A57C33',
                        borderRadius: '8px',
                        color: '#E6D7C3',
                        fontSize: '14px'
                      }}
                    >
                      <option value="Miami">Miami</option>
                      <option value="New York">New York</option>
                      <option value="Los Angeles">Los Angeles</option>
                      <option value="Las Vegas">Las Vegas</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', color: '#D4AF37', marginBottom: '8px', fontSize: '14px' }}>Venue Type *</label>
                    <select
                      required
                      value={conciergeForm.venue_type}
                      onChange={(e) => setConciergeForm({ ...conciergeForm, venue_type: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '12px',
                        background: 'rgba(230, 215, 195, 0.1)',
                        border: '1px solid #A57C33',
                        borderRadius: '8px',
                        color: '#E6D7C3',
                        fontSize: '14px'
                      }}
                    >
                      <option value="club">Nightclub</option>
                      <option value="lounge">Lounge</option>
                      <option value="restaurant">Restaurant</option>
                      <option value="beach_club">Beach Club</option>
                      <option value="rooftop">Rooftop Bar</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', color: '#D4AF37', marginBottom: '8px', fontSize: '14px' }}>Number of Guests *</label>
                    <input
                      type="number"
                      required
                      min="1"
                      value={conciergeForm.num_guests}
                      onChange={(e) => setConciergeForm({ ...conciergeForm, num_guests: parseInt(e.target.value) })}
                      style={{
                        width: '100%',
                        padding: '12px',
                        background: 'rgba(230, 215, 195, 0.1)',
                        border: '1px solid #A57C33',
                        borderRadius: '8px',
                        color: '#E6D7C3',
                        fontSize: '14px'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', color: '#D4AF37', marginBottom: '8px', fontSize: '14px' }}>Budget (Optional)</label>
                    <select
                      value={conciergeForm.budget}
                      onChange={(e) => setConciergeForm({ ...conciergeForm, budget: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '12px',
                        background: 'rgba(230, 215, 195, 0.1)',
                        border: '1px solid #A57C33',
                        borderRadius: '8px',
                        color: '#E6D7C3',
                        fontSize: '14px'
                      }}
                    >
                      <option value="">Select budget</option>
                      <option value="$500-$1000">$500 - $1,000</option>
                      <option value="$1000-$2500">$1,000 - $2,500</option>
                      <option value="$2500-$5000">$2,500 - $5,000</option>
                      <option value="$5000+">$5,000+</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', color: '#D4AF37', marginBottom: '8px', fontSize: '14px' }}>Special Requests</label>
                    <textarea
                      value={conciergeForm.special_requests}
                      onChange={(e) => setConciergeForm({ ...conciergeForm, special_requests: e.target.value })}
                      rows={4}
                      style={{
                        width: '100%',
                        padding: '12px',
                        background: 'rgba(230, 215, 195, 0.1)',
                        border: '1px solid #A57C33',
                        borderRadius: '8px',
                        color: '#E6D7C3',
                        fontSize: '14px',
                        resize: 'vertical'
                      }}
                      placeholder="Any special requirements, preferences, or details..."
                    />
                  </div>

                  <button
                    type="submit"
                    style={{
                      width: '100%',
                      padding: '16px',
                      background: 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)',
                      color: '#2C1B13',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    Submit to Concierge
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EventsPage;